import { use, useEffect, useRef } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { useDelayedValue } from '@gonfalon/react/useDelayedValue';
import {
  Group,
  IconButton,
  Input,
  mergeProps,
  ProgressBar,
  SearchField,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { SearchBoxContext } from './SearchBox.context';
import { type SearchBoxProps } from './SearchBox.types';

export type { SearchBoxProps } from './SearchBox.types';

import styles from './SearchBox.module.css';

export function SearchBox({
  'aria-label': ariaLabel = msg('ui.searchBox.searchLabel'),
  isPending: propsIsPending,
  focusOnMount: propsFocusOnMount,
  placeholder: propsPlaceholder,
  ...props
}: SearchBoxProps) {
  const {
    isPending: searchBoxContextIsPending,
    focusOnMount: searchBoxContextFocusOnMount,
    placeholder: searchBoxContextPlaceholder,
    ...searchBoxContextProps
  } = use(SearchBoxContext);
  const isPending = propsIsPending ?? searchBoxContextIsPending;
  const focusOnMount = propsFocusOnMount ?? searchBoxContextFocusOnMount;
  const placeholder = propsPlaceholder ?? searchBoxContextPlaceholder;

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focusOnMount) {
      inputRef.current?.focus();
    }
  }, [focusOnMount]);

  // delay the loading state to prevent flickering on fast searches
  const delayedIsPending = useDelayedValue(isPending, 50);

  return (
    <SearchField aria-label={ariaLabel} {...mergeProps({ className: styles.SearchBox }, searchBoxContextProps, props)}>
      <Group className={styles.Group}>
        <Icon name="search" size="small" />
        <Input placeholder={placeholder} ref={inputRef} />
        {delayedIsPending ? (
          <ProgressBar isIndeterminate aria-label={msg('ui.searchBox.loadingLabel')} className={styles.ProgressBar} />
        ) : (
          <TooltipTrigger>
            <IconButton
              aria-label={msg('ui.searchBox.clearLabel')}
              icon="cancel-circle-outline"
              size="small"
              variant="minimal"
              className={styles.ClearButton}
            />
            <Tooltip>
              <Message id="ui.searchBox.clear" />
            </Tooltip>
          </TooltipTrigger>
        )}
      </Group>
    </SearchField>
  );
}
